import { useCallback } from "react";

const PENDING_JOBS_KEY = "pending_jobs";
const AI_PROCESSING_KEY = "ai_processing";
const MAX_PENDING = 5;
const MAX_AGE_MS = 5 * 60 * 1000;

const safeParse = (value, fallback) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

export const useAiJobState = () => {
  const loadOptimisticJobs = useCallback(() => {
    const pendingJobs = safeParse(localStorage.getItem(PENDING_JOBS_KEY) || "[]", []);
    const cutoff = Date.now() - MAX_AGE_MS;
    const recent = pendingJobs.filter((job) => {
      const ts = new Date(job.created_at || 0).getTime();
      return ts > cutoff;
    });
    if (recent.length !== pendingJobs.length) {
      localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(recent));
    }
    return recent;
  }, []);

  const addOptimisticJob = useCallback((job) => {
    const pendingJobs = safeParse(localStorage.getItem(PENDING_JOBS_KEY) || "[]", []);
    const next = [...pendingJobs, job].slice(-MAX_PENDING);
    localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(next));
    return job;
  }, []);

  const removeOptimisticJob = useCallback((jobId) => {
    const pendingJobs = safeParse(localStorage.getItem(PENDING_JOBS_KEY) || "[]", []);
    const remaining = pendingJobs.filter((job) => job.id !== jobId);
    localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(remaining));
    return remaining;
  }, []);

  const cleanupOptimisticJobs = useCallback((realJobs) => {
    const realJobIds = new Set((realJobs || []).map((j) => j.id));
    const pendingJobs = safeParse(localStorage.getItem(PENDING_JOBS_KEY) || "[]", []);
    const remaining = pendingJobs.filter((job) => !realJobIds.has(job.id));
    localStorage.setItem(PENDING_JOBS_KEY, JSON.stringify(remaining));
    return remaining;
  }, []);

  const mergeJobs = useCallback((realJobs, optimisticJobs) => {
    const realJobIds = new Set((realJobs || []).map((j) => j.id));
    const nowMs = Date.now();
    const realActiveByType = new Map();
    for (const j of realJobs || []) {
      const status = String(j?.status || "").toLowerCase();
      const active = ["pending", "processing", "running", "queued"].includes(status);
      if (!active) continue;
      const t = String(j?.job_type || "");
      if (!t) continue;
      const ts = new Date(j.created_at || j.updated_at || 0).getTime();
      const prev = realActiveByType.get(t) || 0;
      if (ts > prev) realActiveByType.set(t, ts);
    }

    const uniqueOptimistic = (optimisticJobs || []).filter((job) => {
      if (realJobIds.has(job.id)) return false;
      // Timeout-created optimistic placeholders are not tied to real IDs; once we
      // see a real active job of the same type, hide the placeholder to avoid
      // stale "pending" rows that never progress.
      const id = String(job?.id || "");
      const isTimeoutPlaceholder = id.startsWith("opt_timeout_");
      if (!isTimeoutPlaceholder) return true;
      const t = String(job?.job_type || "");
      const latestRealActiveTs = realActiveByType.get(t) || 0;
      if (!latestRealActiveTs) return true;
      const optimisticTs = new Date(job.created_at || job.updated_at || 0).getTime();
      const closeInTime = Math.abs(latestRealActiveTs - optimisticTs) <= 15 * 60 * 1000;
      if (closeInTime) return false;
      // Also drop old timeout placeholders after a grace window.
      return nowMs - optimisticTs <= 2 * 60 * 1000;
    });
    return [...(realJobs || []), ...uniqueOptimistic].sort((a, b) => {
      const aTime = new Date(a.created_at || a.updated_at || 0).getTime();
      const bTime = new Date(b.created_at || b.updated_at || 0).getTime();
      return bTime - aTime;
    });
  }, []);

  const setAiProcessing = useCallback((active) => {
    const next = active ? "1" : "0";
    const current = localStorage.getItem(AI_PROCESSING_KEY) || "0";
    if (current !== next) {
      localStorage.setItem(AI_PROCESSING_KEY, next);
      window.dispatchEvent(
        new CustomEvent(active ? "aiProcessingStart" : "aiProcessingStop")
      );
    }
  }, []);

  return {
    loadOptimisticJobs,
    addOptimisticJob,
    removeOptimisticJob,
    cleanupOptimisticJobs,
    mergeJobs,
    setAiProcessing,
  };
};

